import { type EntityType } from 'core/domain/EntityType'
import { createDate } from 'core/utils/date'

import { type Carrier } from '../domain/Carrier'
import { type Quote } from '../domain/Quote'
import { type QuoteAccessorial, type StopType } from '../domain/QuoteAccessorial'
import { type QuoteEquipmentType } from '../domain/QuoteEquipmentType'
import { type QuoteItem } from '../domain/QuoteItem'
import { type QuoteMode } from '../domain/QuoteMode'
import { type QuoteStop } from '../domain/QuoteStop'
import { type Shipper } from '../domain/Shipper'
import { type SpotQuote } from '../domain/SpotQuote'
import { type SpotQuoteStatus } from '../domain/SpotQuoteStatus'

export type RawSpotQuote = {
  carrier: RawCarrier
  shipper: RawShipper
  carrier_notes: string | null
  flat_rate: string | null
  notes: string | null
  per_mile_rate: number | null
  quote: RawQuote
  status: SpotQuoteStatus
  uuid: string
  negotiation_note: string
  under_negotiation: boolean
}

export type RawCarrier = {
  id: number
  name: string | null
  email: string | null
  entity_type?: EntityType
}

export type RawShipper = {
  id: number
  email: string
  logo_email_url: string | null
  name: string | null
  phone_number: string | null
  company_name: string | null
}

export type RawQuoteItem = {
  commodity: string | null
  weight: string | null
}

export type RawQuoteStop = {
  address: string | null
  city: string
  country: string
  date: string | null
  state: string
  zipcode: string | null
  full_street_address?: string | null
}

export type RawQuote = {
  bids_deadline: string
  can_receive_proposals: boolean
  equipment_type: QuoteEquipmentType
  items: Array<RawQuoteItem>
  mileage: number | null
  mode: QuoteMode
  notes?: string | null
  shipper_guide_id: string
  shipper_notes: string | null
  stops: Array<RawQuoteStop>
  accessorials?: Array<RawQuoteAccessorial>
}

export type RawItem = {
  commodity: string
  weight: string
}

export type RawStop = {
  city: string
  state: string
  country: string
  zipcode: string
  address: string
  date: string
}

export type RawQuoteAccessorial = {
  name: string
  code: string
  stop_type: Array<StopType>
  modes: Array<QuoteMode>
}

export function mapApiToQuoteItem(quoteItem: RawQuoteItem): QuoteItem {
  return {
    commodity: quoteItem.commodity || undefined,
    weight: quoteItem.weight || undefined,
  }
}

export function mapApiToCarrier(carrier: RawCarrier): Carrier {
  return {
    id: carrier.id,
    name: carrier.name || undefined,
    email: carrier.email || undefined,
    entityType: carrier.entity_type,
  }
}

export function mapApiToShipper(shipper: RawShipper): Shipper {
  return {
    id: shipper.id,
    email: shipper.email,
    logoUrl: shipper.logo_email_url || undefined,
    name: shipper.name || undefined,
    companyName: shipper.company_name || undefined,
    phoneNumber: shipper.phone_number || undefined,
  }
}

export function mapApiToQuoteStop(quoteStop: RawQuoteStop): QuoteStop {
  return {
    address: quoteStop.address || undefined,
    city: quoteStop.city,
    country: quoteStop.country,
    date: quoteStop.date || undefined,
    state: quoteStop.state,
    zipcode: quoteStop.zipcode || undefined,
    fullStreetAddress: quoteStop.full_street_address || undefined,
  }
}

export function mapApiToAccessorial(quoteAccessorial: RawQuoteAccessorial): QuoteAccessorial {
  return {
    name: quoteAccessorial.name,
    code: quoteAccessorial.code,
    stopType: quoteAccessorial.stop_type,
  }
}

export function mapApiToQuote(quote: RawQuote): Quote {
  const items: Array<QuoteItem> = (quote.items ?? []).map(mapApiToQuoteItem)
  const stops: Array<QuoteStop> = (quote.stops ?? []).map(mapApiToQuoteStop)
  const accessorials: Array<QuoteAccessorial> = (quote.accessorials ?? []).map(mapApiToAccessorial)

  return {
    items,
    stops,
    bidsDeadline: createDate(quote.bids_deadline)!,
    canReceiveProposals: quote.can_receive_proposals,
    equipmentType: quote.equipment_type,
    mileage: quote.mileage ?? undefined,
    mode: quote.mode,
    notes: quote.notes || undefined,
    shipperGuideId: quote.shipper_guide_id,
    shipperNotes: quote.shipper_notes || undefined,
    accessorials,
  }
}

export function mapApiToSpotQuote(rawSpotQuote: RawSpotQuote): SpotQuote {
  const carrier: Carrier = mapApiToCarrier(rawSpotQuote.carrier)
  const quote: Quote = mapApiToQuote(rawSpotQuote.quote)
  const shipper: Shipper = mapApiToShipper(rawSpotQuote.shipper)

  const spotQuote: SpotQuote = {
    carrier,
    quote,
    shipper,
    carrierNotes: rawSpotQuote.carrier_notes || undefined,
    flatRate: rawSpotQuote.flat_rate || undefined,
    notes: rawSpotQuote.notes || undefined,
    perMileRate: rawSpotQuote.per_mile_rate ?? undefined,
    status: rawSpotQuote.status,
    uuid: rawSpotQuote.uuid,
    negotiationNote: rawSpotQuote.negotiation_note || undefined,
    underNegotiation: rawSpotQuote.under_negotiation || false,
  }

  return spotQuote
}
